import { familyTierAmount, intervalCopy, pickBillingPrice, planBillingPrices } from '@/core/utils/membershipBilling'
import { FAMILY_MAX_PEOPLE } from '@/core/utils/membershipFamily'

const FAMILY_MIN_PEOPLE = 2
const INTERVAL_MONTHS = { month: 1, quarter: 3, year: 12 }
const INTERVAL_PERIOD = { month: 'mes', quarter: 'trimestre', year: 'año' }

export function formatPrice(value) {
  const amount = Math.round(Number(value) * 100) / 100
  const digits = Number.isInteger(amount) ? 0 : 2
  return amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

const money = formatPrice

export function clampFamilyPeople(people) {
  const count = Math.floor(Number(people))
  if (!Number.isFinite(count)) return FAMILY_MIN_PEOPLE
  return Math.min(FAMILY_MAX_PEOPLE, Math.max(FAMILY_MIN_PEOPLE, count))
}

function offerPrices(catalog, apiPlan, people) {
  const prices = planBillingPrices(apiPlan, catalog.fam ? null : catalog.price)
  if (!catalog.fam) return prices
  return prices.map((price) => ({ ...price, amount: familyTierAmount(price, people) }))
}

function marketPrice(catalog, people) {
  if (catalog.fam) return (catalog.marketBySize || {})[people] || null
  return catalog.marketPrice || null
}

function savingsLine(catalog, interval, amount, monthlyAmount, people) {
  if (interval !== 'month') {
    const saved = monthlyAmount * INTERVAL_MONTHS[interval] - amount
    return saved > 0 ? `Ahorras $${money(saved)} al ${INTERVAL_PERIOD[interval]} frente a pagar mes a mes` : null
  }
  if (catalog.was > amount) return `Ahorras $${money(catalog.was - amount)} al mes`
  const market = marketPrice(catalog, people)
  if (!market || market <= amount) return null
  const per = catalog.fam ? '' : ' por empleado'
  return `Ahorras $${money(market - amount)}${per} al mes frente a otro plan del mercado`
}

/**
 * Precio, forma de pago y ahorro de un plan del catálogo 2027 con los precios del API.
 * @param {Object} catalog plan de HMP_PLANS_2027
 * @param {Object|null} apiPlan plan de get-plans/all
 * @param {{interval?: string, people?: number}} choice
 */
export function planOffer(catalog, apiPlan, { interval = 'month', people } = {}) {
  const familyPeople = catalog.fam ? clampFamilyPeople(people) : null
  const prices = offerPrices(catalog, apiPlan, familyPeople)
  const selected = pickBillingPrice(prices, interval)
  const monthly = pickBillingPrice(prices, 'month')
  const resolvedInterval = selected ? selected.billing_interval : 'month'
  const amount = selected ? Number(selected.amount) : Number(catalog.price) || 0
  const monthlyAmount = monthly ? Number(monthly.amount) : amount
  const showWas = resolvedInterval === 'month' && catalog.was > amount
  return {
    interval: resolvedInterval,
    intervals: prices.map((price) => ({ value: price.billing_interval, name: intervalCopy(price.billing_interval).name })),
    amount,
    suffix: intervalCopy(resolvedInterval).suffix,
    people: familyPeople,
    perPerson: familyPeople ? money(amount / familyPeople) : null,
    unit: catalog.unit || null,
    was: showWas ? catalog.was : null,
    savingsPercent: showWas ? Math.round(((catalog.was - amount) / catalog.was) * 100) : null,
    savings: savingsLine(catalog, resolvedInterval, amount, monthlyAmount, familyPeople),
    prepaidWellness: prices.some((price) => price.billing_interval === 'quarter' || price.billing_interval === 'year'),
  }
}

/**
 * Ruta de compra (Stripe Checkout) con la forma de pago y, en Familiar, las personas.
 * @returns {Object|null}
 */
export function purchaseRoute(apiPlan, { interval = 'month', people } = {}, isFamily = false) {
  if (!apiPlan || apiPlan.id == null) return null
  const query = { billing_interval: interval }
  if (isFamily) query.people = clampFamilyPeople(people)
  return { name: 'comprar-plan', params: { planId: String(apiPlan.id) }, query }
}

const NOT_INCLUDED = '—'

const COMPARISON_ROWS = [
  {
    label: 'Consultas presenciales y por video',
    value: () => 'Ilimitadas, $0',
  },
  {
    label: 'Wellness Visit',
    by: { tusalud: '1 al año', senior: '2 al año', plus: '2 al año + ultrasonido', familiar: '1 al año por adulto', empresas: '2 al año' },
  },
  {
    label: 'Descuento en lo no cubierto',
    value: (catalog, api) => {
      const percent = Number(api && api.uncovered_discount_percentage)
      return Number.isFinite(percent) && percent > 0 ? `${money(percent)}%` : NOT_INCLUDED
    },
  },
  {
    label: 'PAP y PSA',
    by: { senior: 'Incluidos', plus: 'Incluidos', familiar: 'Incluidos (adultos)', empresas: 'Incluidos' },
  },
  {
    label: 'Imágenes con 30% de descuento',
    by: { plus: 'Ultrasonidos y rayos X' },
  },
  {
    label: 'Regalo al tercer mes',
    by: { senior: 'Monitor de presión', plus: 'Glucómetro o monitor' },
  },
]

/**
 * Filas de la tabla comparativa, una columna por plan en el orden recibido.
 * @param {Array<{catalog: Object, api: Object|null}>} plans
 * @returns {Array<{label: string, values: string[]}>}
 */
export function planComparisonRows(plans) {
  return COMPARISON_ROWS.map((row) => ({
    label: row.label,
    values: plans.map(({ catalog, api }) => (row.value ? row.value(catalog, api) : row.by[catalog.id] || NOT_INCLUDED)),
  }))
}
